import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Hook to load leave request page initial data in a single optimized RPC call
 * Replaces 3 sequential queries (leave_types, balance, company_users) with 1 call
 *
 * @param {string} userId - User profile ID
 * @param {string} companyId - Company ID
 * @param {boolean} isAdmin - Whether user has admin permissions
 * @returns {Object} - { data, loading, error, refetch, leaveTypes, balance, companyUsers }
 */
export function useLeaveRequestPage(userId, companyId, isAdmin = false) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  const loadPageData = useCallback(async () => {
    if (!userId || !companyId) {
      console.log('[useLeaveRequestPage] Missing userId or companyId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useLeaveRequestPage] Already loading, skipping...');
      return null;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useLeaveRequestPage] Loading page data with single RPC call...');
      console.time('[useLeaveRequestPage] RPC call duration');

      const currentYear = new Date().getFullYear();

      // Single RPC call replaces 3 sequential queries!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_leave_request_page_init',
        {
          p_user_id: userId,
          p_company_id: companyId,
          p_year: currentYear,
          p_is_admin: isAdmin
        }
      );

      console.timeEnd('[useLeaveRequestPage] RPC call duration');

      if (rpcError) {
        console.error('[useLeaveRequestPage] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useLeaveRequestPage] RPC response:', {
        leaveTypesCount: rpcData?.leave_types?.length || 0,
        hasBalance: !!rpcData?.balance,
        companyUsersCount: rpcData?.company_users?.length || 0
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      console.error('[useLeaveRequestPage] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [userId, companyId, isAdmin]);

  // Auto-load when dependencies change
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Memoized default leave type
  const defaultLeaveType = useMemo(() => {
    const types = data?.leave_types || [];
    return types.length > 0 ? types[0].name : '';
  }, [data?.leave_types]);

  return {
    data,
    loading,
    error,
    refetch: loadPageData,
    // Convenience accessors
    leaveTypes: data?.leave_types || [],
    balance: data?.balance || null,
    companyUsers: data?.company_users || [],
    defaultLeaveType
  };
}

/**
 * Hook to validate leave dates with debouncing
 * Replaces 3 async calls (preview, overlap, users on leave) with 1 debounced call
 *
 * @param {string} userId - User profile ID
 * @param {string} companyId - Company ID
 * @param {number} debounceMs - Debounce delay in milliseconds (default 300ms)
 * @returns {Object} - { validate, validating, validation, resetValidation }
 */
export function useLeaveValidation(userId, companyId, debounceMs = 300) {
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Validate dates with debouncing
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {string} startPeriod - 'full', 'am', or 'pm'
   * @param {string} endPeriod - 'full', 'am', or 'pm'
   */
  const validate = useCallback((startDate, endDate, startPeriod = 'full', endPeriod = 'full') => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Validate inputs
    if (!startDate || !endDate || !userId || !companyId) {
      setValidation(null);
      setValidating(false);
      return;
    }

    setValidating(true);

    // Debounce the API call
    debounceRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();

      try {
        console.log('[useLeaveValidation] Validating dates...');
        console.time('[useLeaveValidation] RPC call duration');

        // Build part_day JSON
        let partDayJson = null;
        if (startPeriod !== 'full' || endPeriod !== 'full') {
          partDayJson = {};
          if (startPeriod !== 'full') {
            partDayJson.start = startPeriod.toUpperCase();
          }
          if (endPeriod !== 'full' && endDate !== startDate) {
            partDayJson.end = endPeriod.toUpperCase();
          }
        }

        // Single RPC call replaces 3 async operations!
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          'validate_leave_dates',
          {
            p_user_id: userId,
            p_company_id: companyId,
            p_start_date: startDate,
            p_end_date: endDate,
            p_part_day: partDayJson
          }
        );

        console.timeEnd('[useLeaveValidation] RPC call duration');

        if (rpcError) {
          console.error('[useLeaveValidation] RPC error:', rpcError);
          setValidation({
            previewDays: null,
            usersOnLeave: [],
            currentUserOverlap: null,
            workingDays: null,
            error: rpcError.message
          });
        } else {
          console.log('[useLeaveValidation] RPC response:', {
            previewDays: rpcData?.preview_days,
            usersOnLeaveCount: rpcData?.users_on_leave_count || 0,
            hasOverlap: rpcData?.has_overlap,
            workingDays: rpcData?.working_days
          });

          setValidation({
            previewDays: rpcData?.preview_days,
            usersOnLeave: (rpcData?.users_on_leave || []).map(user => ({
              userId: user.user_id,
              name: user.name,
              startDate: user.start_date,
              endDate: user.end_date,
              leaveType: user.leave_type,
              status: user.status
            })),
            currentUserOverlap: rpcData?.current_user_overlap !== 'null' ? rpcData?.current_user_overlap : null,
            hasOverlap: rpcData?.has_overlap || false,
            workingDays: rpcData?.working_days || [1, 2, 3, 4, 5],
            error: null
          });
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError') {
          return;
        }
        console.error('[useLeaveValidation] Error:', err);
        setValidation({
          previewDays: null,
          usersOnLeave: [],
          currentUserOverlap: null,
          error: err.message
        });
      } finally {
        setValidating(false);
      }
    }, debounceMs);
  }, [userId, companyId, debounceMs]);

  /**
   * Reset validation state
   */
  const resetValidation = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setValidation(null);
    setValidating(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    validate,
    validating,
    validation,
    resetValidation,
    // Convenience accessors
    previewDays: validation?.previewDays ?? null,
    usersOnLeave: validation?.usersOnLeave || [],
    currentUserOverlap: validation?.currentUserOverlap || null,
    hasOverlap: validation?.hasOverlap || false,
    workingDays: validation?.workingDays || null
  };
}

/**
 * Hook to load balance for a specific user (for admin user switching)
 * @param {string} userId - Target user profile ID
 * @returns {Object} - { balance, loading, error, refetch }
 */
export function useUserBalance(userId) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  const loadBalance = useCallback(async () => {
    if (!userId) {
      setBalance(null);
      setLoading(false);
      return null;
    }

    if (loadingRef.current) {
      return null;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const currentYear = new Date().getFullYear();
      const { data, error: queryError } = await supabase
        .from('leave_balances')
        .select('*')
        .eq('user_id', userId)
        .eq('year', currentYear)
        .single();

      if (queryError && queryError.code !== 'PGRST116') {
        throw queryError;
      }

      setBalance(data || null);
      return data;
    } catch (err) {
      console.error('[useUserBalance] Error:', err);
      setError(err.message);
      setBalance(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  return {
    balance,
    loading,
    error,
    refetch: loadBalance
  };
}
